import React from "react";
//#region types
import { AnnualReportToApproveInformationProps } from "models/propTypes/annualAccountPropTypes";
import { TranslationKeys } from "translations";
import { SimpleTableRow } from "models/propTypes/simpleTableProps";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { SimpleAccordionTable } from "components/common";
import Amount from "./amount";
import useDividendProposal from "./useDividendProposal";
//#endregion
import { CurrencyHelper, LocalizationHelperInstance } from "utils/helpers";

const AnnualReportToApproveInformation: React.FC<AnnualReportToApproveInformationProps> = (props) => {
  const { dividendInformation } = props;

  const maxDividendAmount = dividendInformation.maxAllowedDividend;
  const retainedEarnings = dividendInformation.retainedEarnings;
  const sharePremiumFund = dividendInformation.sharePremiumFund ?? 0;
  const annualReportYearResult = dividendInformation.annualReportYearResult;
  const accountingNonRestrictedEquity = dividendInformation.accountingNonRestrictedEquity;
  const nextYearShareholderContributionRefund = dividendInformation.nextYearShareholderContributionRefund;

  const { hasDividendProposal, dividendProposalRounded: dividendProposal } = useDividendProposal(
    dividendInformation.dividendProposal,
    maxDividendAmount
  );

  const convertToCurrency = (amount: number) => CurrencyHelper.toRoundedDownSEK(amount);

  const dividendInfoItems = React.useMemo(() => {
    const items: SimpleTableRow[] = [
      {
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.PossibleDividend),
        value: <Amount>{convertToCurrency(maxDividendAmount)}</Amount>,
        subContent: [
          ...(sharePremiumFund !== 0
            ? [
                {
                  title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.SharePremiumFund),
                  tooltip: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.AnnualAccount.SharePremiumFundToolTip
                  ),
                  value: <Amount>{convertToCurrency(sharePremiumFund)}</Amount>,
                },
              ]
            : []),
          {
            title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.RetainedEarnings),
            tooltip: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.AnnualAccount.RetainedEarningsToolTip
            ),
            value: <Amount>{convertToCurrency(retainedEarnings)}</Amount>,
          },
          {
            title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.NetEarnings),
            tooltip: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.NetEarningsToolTip),
            value: <Amount>{convertToCurrency(annualReportYearResult)}</Amount>,
          },
          {
            title: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.AnnualAccount.RepaymentOfShareholderContribution
            ),
            tooltip: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.AnnualAccount.RepaymentOfShareholderContributionToolTip
            ),
            value: <Amount>{convertToCurrency(nextYearShareholderContributionRefund)}</Amount>,
          },
          {
            title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.ChangeInEquity),
            tooltip: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.AnnualAccount.ChangeInEquityToolTip
            ),
            value: <Amount>{convertToCurrency(accountingNonRestrictedEquity)}</Amount>,
          },
        ],
      },
    ];

    // Fall back to the legacy wintK10 field if the new one is missing (deployment-window safety).
    const allK10VerifiedByWint = dividendInformation.allK10VerifiedByWint ?? dividendInformation.wintK10;

    items.push({
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.LowTaxedDividend),
      value: hasDividendProposal ? (
        <Amount displayType={!allK10VerifiedByWint ? StyleEnums.DisplayType.Warning : undefined}>
          {convertToCurrency(dividendProposal!)}
        </Amount>
      ) : (
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.DividendUnknown)
      ),
      subContent: !allK10VerifiedByWint
        ? [
            {
              title: LocalizationHelperInstance.getFormattedMessage(
                hasDividendProposal
                  ? TranslationKeys.AnnualAccount.K10NotByWintWarning
                  : TranslationKeys.AnnualAccount.K10NotByWintForAllShareholdersWarning
              ),
            },
          ]
        : undefined,
    });

    return items;
  }, [dividendInformation]);

  return (
    <Grid container rowGap={1}>
      <SimpleAccordionTable items={dividendInfoItems} />
    </Grid>
  );
};

export default AnnualReportToApproveInformation;
